import { supabase } from '../lib/supabaseClient';
import {
  AttendanceClaimResult,
  CardInventoryResponse,
  DailyClaimResult,
  GachaDrawRequest,
  GachaDrawResult,
} from '../types/cards';

const INVENTORY_FUNCTION = 'get-card-inventory';
const DAILY_CLAIM_FUNCTION = 'claim-daily-coin';
const ATTENDANCE_FUNCTION = 'claim-attendance';
const GACHA_FUNCTION = 'gacha-draw';

async function invokeFunction<T>(
  name: string,
  body?: Record<string, unknown>
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, {
    body,
  });

  if (error) {
    // Supabase Functions 에러에서 실제 에러 메시지 추출
    let errorMessage: string = typeof error.message === 'string' 
      ? error.message 
      : String(error.message || '알 수 없는 오류가 발생했습니다.');
    
    // Supabase 에러 객체의 다양한 필드에서 실제 응답 본문 찾기
    const errorAny = error as typeof error & { 
      context?: { body?: string; message?: string; error?: string; status?: number } | string;
      details?: { error?: string; message?: string } | string;
      [key: string]: unknown;
    };
    
    // 1. error.details 확인 (Supabase에서 실제 응답 본문이 여기 있을 수 있음)
    if (errorAny.details) {
      try {
        let details: unknown = errorAny.details;
        
        if (typeof details === 'string') {
          details = JSON.parse(details);
        }
        
        if (details && typeof details === 'object') {
          if ('error' in details && typeof (details as { error?: unknown }).error === 'string') {
            errorMessage = (details as { error: string }).error;
          } else if ('message' in details && typeof (details as { message?: unknown }).message === 'string') {
            errorMessage = (details as { message: string }).message;
          }
        }
      } catch {
        // 파싱 실패 시 무시
      }
    }
    
    // 2. error.context 확인 (Response 객체)
    if (errorAny.context) {
      try {
        // context가 Response 객체인 경우
        if (errorAny.context instanceof Response) {
          const response = errorAny.context;
          
          // Response 본문을 읽기 (bodyUsed 체크 후 clone 사용)
          try {
            // Response를 clone해서 본문을 안전하게 읽기
            const clonedResponse = response.clone();
            const responseData = await clonedResponse.json();
            
            if (responseData.error && typeof responseData.error === 'string') {
              errorMessage = responseData.error;
            } else if (responseData.message && typeof responseData.message === 'string') {
              errorMessage = responseData.message;
            }
          } catch (jsonError) {
            // JSON 파싱 실패 시 텍스트로 읽기 시도
            try {
              const clonedResponse = response.clone();
              const responseText = await clonedResponse.text();
              if (responseText) {
                try {
                  const parsed = JSON.parse(responseText);
                  if (parsed.error && typeof parsed.error === 'string') {
                    errorMessage = parsed.error;
                  } else if (parsed.message && typeof parsed.message === 'string') {
                    errorMessage = parsed.message;
                  }
                } catch {
                  // JSON 파싱 실패 시 원본 텍스트 사용
                  errorMessage = responseText;
                }
              }
            } catch (textError) {
              // 텍스트 읽기 실패 시 무시
            }
          }
        } else {
          // context가 객체나 문자열인 경우 (기존 로직)
          let context: unknown = errorAny.context;
          
          if (typeof context === 'string') {
            context = JSON.parse(context);
          }
          
          if (context && typeof context === 'object') {
            // context.body에서 응답 본문 파싱
            if ('body' in context) {
              const bodyStr = (context as { body?: unknown }).body;
              if (typeof bodyStr === 'string') {
                try {
                  const parsed = JSON.parse(bodyStr);
                  if (parsed.error && typeof parsed.error === 'string') {
                    errorMessage = parsed.error;
                  } else if (parsed.message && typeof parsed.message === 'string') {
                    errorMessage = parsed.message;
                  }
                } catch {
                  // JSON 파싱 실패 시 무시
                }
              }
            }
            
            // context.error 직접 확인
            if ('error' in context && typeof (context as { error?: unknown }).error === 'string') {
              errorMessage = (context as { error: string }).error;
            }
            
            // context.message 확인
            if ('message' in context && typeof (context as { message?: unknown }).message === 'string') {
              errorMessage = (context as { message: string }).message;
            }
          }
        }
      } catch {
        // 파싱 실패 시 무시
      }
    }
    
    // 에러 메시지에 JSON이 포함된 경우 파싱 (문자열인지 확인)
    if (typeof errorMessage === 'string' && errorMessage.includes('{') && errorMessage.includes('}')) {
      try {
        const jsonMatch = errorMessage.match(/\{.*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.error && typeof parsed.error === 'string') {
            errorMessage = parsed.error;
          } else if (parsed.message && typeof parsed.message === 'string') {
            errorMessage = parsed.message;
          }
        }
      } catch {
        // JSON 파싱 실패 시 원본 메시지 사용
      }
    }
    
    // 실제 에러 메시지로 새 Error 객체 생성 (문자열로 보장)
    const customError = new Error(String(errorMessage));
    customError.name = error.name || 'FunctionError';
    throw customError;
  }

  if (!data) {
    throw new Error(`No data returned from ${name}`);
  }

  return data;
}

export async function fetchCardInventory(): Promise<CardInventoryResponse> {
  return invokeFunction<CardInventoryResponse>(INVENTORY_FUNCTION);
}

export async function claimDailyCoins(): Promise<DailyClaimResult> {
  return invokeFunction<DailyClaimResult>(DAILY_CLAIM_FUNCTION);
}

export async function claimAttendance(): Promise<AttendanceClaimResult> {
  return invokeFunction<AttendanceClaimResult>(ATTENDANCE_FUNCTION);
}

export async function drawGacha(
  payload: GachaDrawRequest
): Promise<GachaDrawResult> {
  return invokeFunction<GachaDrawResult>(GACHA_FUNCTION, {
    pulls: payload.pulls,
  });
}


